/**
 * Environment Banner
 *
 * Persistent top-of-viewport banner shown in any non-production environment
 * (UAT, staging, dev). Reads VITE_ENVIRONMENT, which is baked into the bundle
 * at build time by the deploy workflow. Hidden in production.
 *
 * The banner exists so that QA, demos, and dogfooding can never confuse a
 * test environment with the live one. Real data lives in production only.
 */
import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '@kantera/ui';

const ENV = (import.meta.env.VITE_ENVIRONMENT ?? '').toLowerCase();

const isNonProd = ENV !== '' && ENV !== 'production' && ENV !== 'prod';

const BANNER_HEIGHT = 28;

const BAR_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: BANNER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing.sm,
  padding: `0 ${theme.spacing.lg}`,
  background: theme.colors.warning.primary,
  color: '#08080d',
  fontSize: theme.typography.fontSize.xs.size,
  lineHeight: theme.typography.fontSize.xs.lineHeight,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  zIndex: theme.zIndex.tooltip + 10,
  boxShadow: theme.shadows.md,
  pointerEvents: 'none',
  userSelect: 'none',
};

const DOT_STYLE: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: '#08080d',
  animation: 'envBannerPulse 2s ease-in-out infinite',
};

export const ENV_BANNER_HEIGHT = isNonProd ? BANNER_HEIGHT : 0;

export function EnvBanner() {
  const { t } = useTranslation();
  if (!isNonProd) return null;

  const label = ENV === 'uat' ? 'UAT' : ENV.toUpperCase();

  // envBannerPulse keyframes live in apps/web/src/index.css (global) so this
  // component can stay pure JSX without an inline stylesheet (Law 6 — inline tokens).
  return (
    <div role="status" aria-live="polite" style={BAR_STYLE}>
      <span style={DOT_STYLE} aria-hidden />
      <span>
        {t('env.banner', { env: label, defaultValue: '{{env}} environment — not real data' })}
      </span>
    </div>
  );
}
